import { useState, useEffect, useRef } from 'react';
import { Card, toast } from '@/components';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import styles from './Profile.module.css';

interface AdminProfile {
  id: string;
  name: string;
  name_last: string;
  name_first: string;
  kana_last: string;
  kana_first: string;
  email: string;
  phone: string | null;
  role: string;
}

const KANA_RE = /^[ァ-ヶー　\s]*$/;
const PHONE_RE = /^[0-9\-+() ]*$/;

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  const score = [/.{8,}/, /[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(password)).length;
  const labels = ['', '弱い', '弱い', '普通', '強い', '非常に強い'];
  const colors = ['', '#ef4444', '#f97316', '#eab308', '#22c55e', '#16a34a'];
  return (
    <div className={styles.strengthRow}>
      <div className={styles.strengthBars}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={styles.strengthBar} style={{ background: i <= score ? colors[score] : 'var(--border)' }} />
        ))}
      </div>
      <span style={{ fontSize: 11, color: colors[score] }}>{labels[score]}</span>
    </div>
  );
}

export function Profile() {
  const { user, updateUser } = useAuthStore();

  // ── 基本情報 ──────────────────────────────────
  const [nameLast, setNameLast] = useState('');
  const [nameFirst, setNameFirst] = useState('');
  const [kanaLast, setKanaLast] = useState('');
  const [kanaFirst, setKanaFirst] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // ── メール変更 ──────────────────────────────────
  const [newEmail, setNewEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeInputVisible, setCodeInputVisible] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── パスワード変更 ────────────────────────────────
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  // ── 初期ロード ────────────────────────────────
  useEffect(() => {
    const nameParts = (user?.name ?? '').split(' ');
    const fallbackLast = nameParts[0] ?? '';
    const fallbackFirst = nameParts.slice(1).join(' ') || '';

    api<AdminProfile>('/console/admin/profile')
      .then((p) => {
        setNameLast(p.name_last || fallbackLast);
        setNameFirst(p.name_first || fallbackFirst);
        setKanaLast(p.kana_last ?? '');
        setKanaFirst(p.kana_first ?? '');
        setPhone(p.phone ?? '');
        setNewEmail(p.email || user?.email || '');
        setEmailVerified(false);
      })
      .catch(() => {
        setNameLast(fallbackLast);
        setNameFirst(fallbackFirst);
        setNewEmail(user?.email ?? '');
      });
  }, []);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  const startCooldown = () => {
    setCooldown(60);
    cooldownRef.current = setInterval(() => {
      setCooldown((v) => {
        if (v <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return v - 1;
      });
    }, 1000);
  };

  const cancelPw = () => {
    setPwOpen(false);
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setShowPw(false);
  };

  // ── バリデーション（基本情報） ──────────────────────
  const infoErrors: Record<string, string> = {};
  if (!nameLast) infoErrors.nameLast = '必須';
  else if (nameLast.length > 10) infoErrors.nameLast = '10文字以内';
  if (!nameFirst) infoErrors.nameFirst = '必須';
  else if (nameFirst.length > 10) infoErrors.nameFirst = '10文字以内';
  if (!kanaLast) infoErrors.kanaLast = '必須';
  else if (!KANA_RE.test(kanaLast)) infoErrors.kanaLast = '全角カタカナのみ';
  if (!kanaFirst) infoErrors.kanaFirst = '必須';
  else if (!KANA_RE.test(kanaFirst)) infoErrors.kanaFirst = '全角カタカナのみ';
  if (phone && !PHONE_RE.test(phone)) infoErrors.phone = '数字・ハイフン形式';

  // ── バリデーション（パスワード） ──────────────────────
  const pwErrors: Record<string, string> = {};
  if (!currentPw) pwErrors.currentPw = '必須';
  if (!newPw) pwErrors.newPw = '必須';
  else if (newPw.length < 8) pwErrors.newPw = '8文字以上';
  if (newPw && confirmPw !== newPw) pwErrors.confirmPw = 'パスワードが一致しません';

  // ── 基本情報保存 ────────────────────────────────
  const handleSave = async () => {
    if (Object.keys(infoErrors).length > 0) {
      toast(Object.values(infoErrors)[0], 'error');
      return;
    }
    setSaving(true);
    try {
      const updated = await api<AdminProfile>('/console/admin/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          name_last: nameLast,
          name_first: nameFirst,
          kana_last: kanaLast,
          kana_first: kanaFirst,
          phone: phone || null,
        }),
      });
      updateUser({ name: `${updated.name_last} ${updated.name_first}` });
      setEditing(false);
      toast('基本情報を更新しました', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── 認証メール送信 ────────────────────────────────
  const handleSendVerify = async () => {
    if (!newEmail || cooldown > 0) return;
    setEmailSending(true);
    setCodeInputVisible(false);
    setVerifyCode('');
    setEmailVerified(false);
    try {
      await api('/console/admin/email/verify-request', {
        method: 'POST',
        body: JSON.stringify({ newEmail }),
      });
      toast(`${newEmail} に認証コードを送信しました`, 'success');
      startCooldown();
      setCodeInputVisible(true);
    } catch (e) {
      toast(e instanceof Error ? e.message : '送信に失敗しました', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  // ── 認証コード確認 ────────────────────────────────
  const handleVerifyCode = async () => {
    if (!verifyCode) { toast('認証コードを入力してください', 'error'); return; }
    setVerifying(true);
    try {
      await api('/console/admin/email/verify-confirm', {
        method: 'POST',
        body: JSON.stringify({ code: verifyCode, newEmail }),
      });
      setEmailVerified(true);
      setCodeInputVisible(false);
      // メールアドレスを保存
      await api<AdminProfile>('/console/admin/profile', {
        method: 'PATCH',
        body: JSON.stringify({ email: newEmail }),
      });
      updateUser({ email: newEmail });
      toast('メールアドレスを変更しました', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : '認証コードが正しくありません', 'error');
    } finally {
      setVerifying(false);
    }
  };

  // ── パスワード変更 ────────────────────────────────
  const handleChangePassword = async () => {
    if (Object.keys(pwErrors).length > 0) {
      toast(Object.values(pwErrors)[0], 'error');
      return;
    }
    setPwSaving(true);
    try {
      await api('/console/admin/profile', {
        method: 'PATCH',
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      toast('パスワードを変更しました', 'success');
      cancelPw();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'パスワード変更に失敗しました', 'error');
    } finally {
      setPwSaving(false);
    }
  };

  const emailChanged = newEmail !== (user?.email ?? '');

  return (
    <div className={styles.page}>
      {/* ① 基本情報 */}
      <Card title="基本情報" actions={
        editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={styles.cancelBtn} onClick={() => setEditing(false)}>キャンセル</button>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? '保存中…' : '保存する'}
            </button>
          </div>
        ) : (
          <button className={styles.editBtn} onClick={() => setEditing(true)}>編集</button>
        )
      }>
        {editing ? (
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <label className={styles.label}>姓 <span className={styles.req}>*</span></label>
              <input
                className={`${styles.input} ${infoErrors.nameLast ? styles.inputError : ''}`}
                value={nameLast}
                onChange={(e) => setNameLast(e.target.value)}
                maxLength={10}
                placeholder="田村"
              />
              {infoErrors.nameLast && <span className={styles.errorMsg}>{infoErrors.nameLast}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label}>名 <span className={styles.req}>*</span></label>
              <input
                className={`${styles.input} ${infoErrors.nameFirst ? styles.inputError : ''}`}
                value={nameFirst}
                onChange={(e) => setNameFirst(e.target.value)}
                maxLength={10}
                placeholder="健一"
              />
              {infoErrors.nameFirst && <span className={styles.errorMsg}>{infoErrors.nameFirst}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label}>フリガナ（セイ）<span className={styles.req}>*</span></label>
              <input
                className={`${styles.input} ${infoErrors.kanaLast ? styles.inputError : ''}`}
                value={kanaLast}
                onChange={(e) => setKanaLast(e.target.value)}
                placeholder="タムラ"
              />
              {infoErrors.kanaLast && <span className={styles.errorMsg}>{infoErrors.kanaLast}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label}>フリガナ（メイ）<span className={styles.req}>*</span></label>
              <input
                className={`${styles.input} ${infoErrors.kanaFirst ? styles.inputError : ''}`}
                value={kanaFirst}
                onChange={(e) => setKanaFirst(e.target.value)}
                placeholder="ケンイチ"
              />
              {infoErrors.kanaFirst && <span className={styles.errorMsg}>{infoErrors.kanaFirst}</span>}
            </div>
            <div className={`${styles.field} ${styles.fieldFull}`}>
              <label className={styles.label}>電話番号</label>
              <input
                className={`${styles.input} ${infoErrors.phone ? styles.inputError : ''}`}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="03-1234-5678"
                type="tel"
              />
              {infoErrors.phone && <span className={styles.errorMsg}>{infoErrors.phone}</span>}
            </div>
          </div>
        ) : (
          <div className={styles.infoDisplay}>
            <div className={styles.infoRow}><span className={styles.infoLabel}>姓</span><span className={styles.infoValue}>{nameLast || '—'}</span></div>
            <div className={styles.infoRow}><span className={styles.infoLabel}>名</span><span className={styles.infoValue}>{nameFirst || '—'}</span></div>
            <div className={styles.infoRow}><span className={styles.infoLabel}>フリガナ（セイ）</span><span className={styles.infoValue}>{kanaLast || '—'}</span></div>
            <div className={styles.infoRow}><span className={styles.infoLabel}>フリガナ（メイ）</span><span className={styles.infoValue}>{kanaFirst || '—'}</span></div>
            <div className={styles.infoRow}><span className={styles.infoLabel}>電話番号</span><span className={styles.infoValue}>{phone || '—'}</span></div>
            <div className={styles.infoRow}><span className={styles.infoLabel}>メール</span><span className={styles.infoValue}>{newEmail || user?.email || '—'}</span></div>
            <div className={styles.infoRow}><span className={styles.infoLabel}>ロール</span><span className={styles.infoValue}>{user?.role || '—'}</span></div>
          </div>
        )}
      </Card>

      {/* ② メールアドレス */}
      <Card title="メールアドレス">
        <div className={styles.emailSection}>
          <div className={styles.field}>
            <label className={styles.label}>メールアドレス</label>
            <div className={styles.emailRow}>
              <input
                className={`${styles.input} ${emailChanged && !emailVerified && codeInputVisible ? styles.inputError : ''}`}
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailVerified(false);
                  setCodeInputVisible(false);
                  setVerifyCode('');
                }}
                type="email"
                placeholder="admin@example.com"
                disabled={emailVerified}
              />
              {emailVerified && <span className={styles.verified}>✓ 変更済み</span>}
              {!emailVerified && emailChanged && (
                <button
                  className={styles.verifyBtn}
                  onClick={handleSendVerify}
                  disabled={emailSending || cooldown > 0}
                >
                  {cooldown > 0 ? `再送 ${cooldown}秒` : emailSending ? '送信中…' : '認証メール送信'}
                </button>
              )}
            </div>
            {emailChanged && !emailVerified && !codeInputVisible && cooldown === 0 && (
              <p className={styles.hint}>メールアドレスを変更する場合は認証が必要です。</p>
            )}
          </div>

          {/* 認証コード入力 */}
          {codeInputVisible && !emailVerified && (
            <div className={styles.field}>
              <label className={styles.label}>認証コード</label>
              <div className={styles.codeRow}>
                <input
                  className={styles.codeInput}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                />
                <button
                  className={styles.confirmBtn}
                  onClick={handleVerifyCode}
                  disabled={verifying || verifyCode.length < 6}
                >
                  {verifying ? '確認中…' : '認証する'}
                </button>
              </div>
              <p className={styles.hint}>
                {newEmail} に送信した6桁のコードを入力してください。
                {import.meta.env.VITE_USE_MOCKS === 'true' && (
                  <span style={{ color: 'var(--amber)', marginLeft: 6 }}>【モック】コード: 123456</span>
                )}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* ③ パスワード変更 */}
      <Card title="パスワード変更">
        {!pwOpen ? (
          <button className={styles.accordionToggle} onClick={() => setPwOpen(true)}>
            <span>🔑 パスワードを変更する</span>
            <span className={styles.accordionArrow}>▶</span>
          </button>
        ) : (
          <div className={styles.accordionBody}>
            <div className={styles.field}>
              <label className={styles.label}>現在のパスワード <span className={styles.req}>*</span></label>
              <div className={styles.pwRow}>
                <input
                  className={`${styles.input} ${pwErrors.currentPw ? styles.inputError : ''}`}
                  type={showPw ? 'text' : 'password'}
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  autoComplete="current-password"
                />
                <button className={styles.showPwBtn} onClick={() => setShowPw((v) => !v)} type="button">
                  {showPw ? '🙈' : '👁'}
                </button>
              </div>
              {pwErrors.currentPw && <span className={styles.errorMsg}>{pwErrors.currentPw}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label}>新しいパスワード <span className={styles.req}>*</span></label>
              <div className={styles.pwRow}>
                <input
                  className={`${styles.input} ${pwErrors.newPw ? styles.inputError : ''}`}
                  type={showPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <PasswordStrength password={newPw} />
              {pwErrors.newPw && <span className={styles.errorMsg}>{pwErrors.newPw}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label}>確認用パスワード <span className={styles.req}>*</span></label>
              <input
                className={`${styles.input} ${pwErrors.confirmPw ? styles.inputError : ''}`}
                type={showPw ? 'text' : 'password'}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
              />
              {pwErrors.confirmPw && <span className={styles.errorMsg}>{pwErrors.confirmPw}</span>}
            </div>
            <div className={styles.pwActions}>
              <button className={styles.cancelBtn} onClick={cancelPw} disabled={pwSaving}>
                キャンセル
              </button>
              <button className={styles.saveBtn} onClick={handleChangePassword} disabled={pwSaving}>
                {pwSaving ? '変更中…' : '変更する'}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
